import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { DateTime } from 'luxon';
import { Notification } from '../../interfaces/notification';
import * as NotificationActions from './notifications.actions';

export const notificationsFeatureKey = 'notifications';

export interface State extends EntityState<Notification> {
  loading: boolean;
  lastPoll: DateTime;
  overall: number;
  unseen: number;
  error: any;
}

export function selectNotificationId(notification: Notification): string {
  return notification.id + notification.type;
}

export const adapter: EntityAdapter<Notification> = createEntityAdapter<Notification>({
  selectId: selectNotificationId,
});

export const initialState: State = adapter.getInitialState({
  loading: false,
  lastPoll: undefined,
  overall: 0,
  unseen: 0,
  error: null,
});

export const notificationsReducer = createReducer(
  initialState,
  on(NotificationActions.loadNotificationsLazy, (state) => ({ ...state, loading: true })),
  on(NotificationActions.loadNotificationsPolling, (state) => ({ ...state, loading: true })),
  on(NotificationActions.loadNotificationsLazySuccess, (state, action) => {
    return adapter.upsertMany(action.notifications, {
      ...state,
      loading: false,
      unseen: action.unseen,
      overall: action.overall,
      lastPoll: action.lastPoll.plus({ seconds: 1 }),
    });
  }),

  on(NotificationActions.setInitialState, (state, action) => ({ ...initialState, loading: true })),

  on(NotificationActions.loadNotificationsPollingSuccess, (state, action) =>
    adapter.upsertMany(action.notifications, {
      ...state,
      loading: false,
      unseen: state.unseen + action.unseen,
      overall: state.overall + action.overall,
      lastPoll: action.lastPoll.plus({ seconds: 1 }),
    })
  ),
  on(NotificationActions.loadNotificationsFailure, (state, action) => ({
    ...state,
    error: action.error,
    loading: false,
  })),
  on(NotificationActions.toggleNotificationStateSuccess, (state, action) =>
    adapter.upsertOne(
      {
        ...action.notification,
        seen: !action.notification.seen,
      },
      { ...state, unseen: state.unseen + (action.notification.seen ? 1 : -1) }
    )
  ),
  on(NotificationActions.toggleNotificationStateFailure, (state, action) => ({ ...state, error: action.error })),
  on(NotificationActions.markAllNotificationsAsSeenSuccess, (state) =>
    adapter.map((notification) => ({ ...notification, seen: true }), { ...state, unseen: 0 })
  ),
  on(NotificationActions.markAllNotificationsAsSeenFailure, (state, action) => ({ ...state, error: action.error })),
  on(NotificationActions.clearNotifications, () => adapter.removeAll(initialState))
);

export const selectNotificationState = createFeatureSelector<State>(notificationsFeatureKey);

const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors(selectNotificationState);

export const selectNotificationsAll = createSelector(selectAll, (notifications) =>
  notifications.sort((a, b) => DateTime.fromISO(a.timestamp).toSeconds() - DateTime.fromISO(b.timestamp).toSeconds())
);
export const selectNotificationsTotal = selectTotal;
export const selectNotificationsEntities = selectEntities;
export const selectNotificationsIds = selectIds;

export const selectNotificationsLoading = createSelector(selectNotificationState, (state) => state.loading);
export const selectNotificationsUnseen = createSelector(selectNotificationState, (state) => state.unseen);
export const selectNotificationsOverall = createSelector(selectNotificationState, (state) => state.overall);
export const selectNotificationsLazyParameters = createSelector(selectNotificationsTotal, (total) => ({
  offset: total,
  limit: 20,
}));
export const selectNotificationsPollingParameters = createSelector(selectNotificationState, (state) => ({
  offset: 0, //optional im backend machen
  limit: 20, // ^
  lastPoll: state.lastPoll,
}));
